import { useState, useRef, useEffect } from 'react';
import VimeoPlayer from '@vimeo/player';
import qs from 'query-string';

import sleep from '../../../../../util/sleep';
import getLogger from '../../../../../util/logger';
import { VIDEO_PROVIDER } from '../../../../../constants/videos';

const logger = getLogger('VideoSlideshowPlayback');

const DEFAULT_RETRY_ATTEMPT_SECONDS = 1;
const MAX_RETRY_ATTEMPTS = 3;

/**
 * Video Slideshow Playback hook.
 *
 * @param {Object} options The options.
 * @param {Array} [options.videos] The collection of videos
 * @param {Object} [options.video] The current active video
 * @param {Number} [options.nextVideoId] The ID of the next video to be displayed
 * @param {function} [options.onChangeVideo] Function to call once the time for the next video to be displayed comes
 * @param {function} [options.onPlayVideo] Function to call once a given video plays
 * @param {function} [options.onLoadedVideo] Function to call once a given video loads
 * @param {boolean} [options.allowAutoPlay] Determines if auto play should be allowed
 * @param {boolean} [options.allowControls] Determines if controls should be allowed
 */
const useVideoSlideshowPlayback = ({
  videos,
  video: currentVideo,
  nextVideoId,
  onChangeVideo,
  onPlayVideo,
  onLoadedVideo,
  allowAutoPlay = true,
  allowControls = false,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [elapsedPercent, setElapsedPercent] = useState(0);
  const [hasBeenSetup, setHasBeenSetup] = useState(false);
  const [retries, setRetries] = useState(MAX_RETRY_ATTEMPTS);
  const videoPlayerRef = useRef();
  const providerPlayer = useRef();

  const getVideoQueryParams = () => {
    return qs.stringify({
      playsinline: 1,
      badge: 0,
      pip: 0,
      autoplay: allowAutoPlay ? 1 : 0,
      controls: allowControls ? 1 : 0,
    });
  };

  const isVimeo =
    !currentVideo?.video_provider ||
    currentVideo?.video_provider === VIDEO_PROVIDER.VIMEO;

  const onSetupBunnyPlayer = () => {
    if (!videoPlayerRef?.current || !window.playerjs) {
      return;
    }

    // Update iframe src for the current video
    const embedUrl = `${currentVideo.embed_url}?${qs.stringify({
      autoplay: allowAutoPlay,
      preload: true,
    })}`;
    videoPlayerRef.current.src = embedUrl;

    const bunnyPlayer = new window.playerjs.Player(videoPlayerRef.current);
    providerPlayer.current = bunnyPlayer;

    bunnyPlayer.on('ready', () => {
      setHasBeenSetup(true);
      setElapsedPercent(0);
      onLoadedVideo?.();

      bunnyPlayer.on('play', () => {
        setIsPlaying(true);
        onPlayVideo?.();
      });
      bunnyPlayer.on('pause', () => {
        setIsPlaying(false);
      });
      bunnyPlayer.on('ended', () => {
        if (allowAutoPlay) {
          onChangeVideo({ nextVideoId });
        } else {
          setIsPlaying(false);
        }
      });
      bunnyPlayer.on('timeupdate', ({ seconds, duration: dur }) => {
        if (dur > 0) {
          setElapsedPercent(seconds / dur);
        }
      });

      if (allowAutoPlay) {
        bunnyPlayer.play();
      }
    });
  };

  const onSetupProviderPlayer = () => {
    if (!videoPlayerRef?.current) {
      return;
    }

    if (!isVimeo) {
      onSetupBunnyPlayer();
      return;
    }

    // Vimeo
    if (!providerPlayer.current) {
      providerPlayer.current = new VimeoPlayer(videoPlayerRef.current);
    }
    setHasBeenSetup(true);
    const videoUrl = `${currentVideo.embed_url}&${getVideoQueryParams()}`;
    providerPlayer.current.loadVideo(videoUrl);
    // Reset Events
    providerPlayer.current.off('play');
    providerPlayer.current.off('pause');
    providerPlayer.current.off('ended');
    providerPlayer.current.off('loaded');
    providerPlayer.current.off('timeupdate');
    providerPlayer.current.off('error');
    // Setup Events
    providerPlayer.current.on('play', () => {
      setIsPlaying(true);
      onPlayVideo?.();
    });
    providerPlayer.current.on('pause', () => {
      setIsPlaying(false);
    });
    providerPlayer.current.on('ended', () => {
      if (allowAutoPlay) {
        onChangeVideo({ nextVideoId });
      } else {
        setIsPlaying(false);
      }
    });
    providerPlayer.current.on('loaded', () => {
      setElapsedPercent(0);
      onLoadedVideo?.();
    });
    providerPlayer.current.on('timeupdate', ({ percent }) => {
      setElapsedPercent(percent);
    });
    providerPlayer.current.on('error', e => {
      logger.error('VideoSlideshowPlayback::error', e);
      setIsPlaying(false);
    });
  };

  const onPlaybackStop = async ({ resetElapsedPercent } = {}) => {
    try {
      if (isVimeo) {
        await providerPlayer?.current?.pause();
      } else if (providerPlayer?.current) {
        providerPlayer.current.pause();
      }
    } catch (e) {
      // Ignore errors when pausing
    }
    if (resetElapsedPercent) {
      setElapsedPercent(0);
    }
  };

  const onPlaybackStart = async () => {
    // If we have no videos, reset everything and stop
    if ((videos?.length ?? 0) === 0) {
      onPlaybackStop({ resetElapsedPercent: true });
      return;
    }

    try {
      if (isVimeo) {
        await providerPlayer?.current?.play();
      } else if (providerPlayer?.current) {
        providerPlayer.current.play();
      }
    } catch (e) {
      // Assuming an error, stop the playback
      onPlaybackStop({ resetElapsedPercent: true });
      setIsPlaying(false);
    }
  };

  const onThumbnailClick = ({ videoId }) => {
    onPlaybackStop({ resetElapsedPercent: true });
    // Ignore the click if we click on the active slide
    if (videoId === currentVideo?.id) {
      return;
    }

    onChangeVideo({ nextVideoId: videoId, isPlaying: false });
  };

  useEffect(() => {
    if (!isVimeo && currentVideo && videoPlayerRef?.current) {
      onSetupProviderPlayer();
      return;
    }

    if (currentVideo && providerPlayer?.current) {
      onSetupProviderPlayer();
      if (allowAutoPlay) {
        onPlaybackStart();
      }
    }
  }, [currentVideo, videoPlayerRef?.current, nextVideoId]);

  useEffect(() => {
    if (!isVimeo) {
      return;
    }

    const onPrepareVimeoPlayer = async () => {
      if (!hasBeenSetup && retries > 0 && !providerPlayer?.current) {
        try {
          onSetupProviderPlayer();
          if (currentVideo && allowAutoPlay) {
            onPlaybackStart();
          }
        } catch {
          // Force retry if possible
          if (retries > 0) {
            await sleep(DEFAULT_RETRY_ATTEMPT_SECONDS * 1000);
            setRetries(prev => prev - 1);
          }
        }
      }
    };

    onPrepareVimeoPlayer();
  }, [videoPlayerRef?.current, retries]);

  return {
    isPlaying,
    isVimeo,
    elapsedPercent,
    videoPlayerRef,
    providerPlayer,
    onThumbnailClick,
    onPlaybackStart,
    onPlaybackStop,
  };
};

export default useVideoSlideshowPlayback;
